import React from 'react';

/**
 * Top-level error boundary so a runtime crash inside any page renders a
 * clear error card instead of a blank white screen.
 *
 * Logs the error + component stack to the console, and shows the user a
 * minimal "Reload" UI plus the error text (helpful for debugging).
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[JustDeliver UI crash]', error, info);
    this.setState({ info });
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null, info: null });
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const message =
      (this.state.error && (this.state.error.message || String(this.state.error))) ||
      'Something went wrong rendering this page.';

    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
        data-testid="error-boundary"
      >
        <div className="max-w-md w-full bg-white border border-red-200 rounded-xl p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-red-700 mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-600 mb-4">
            JustDeliver hit an unexpected error while rendering this page. Reloading usually fixes it.
            If the issue persists, please share the error message below with support.
          </p>
          <pre
            className="text-xs bg-gray-50 border border-gray-200 rounded p-2 overflow-auto whitespace-pre-wrap break-words mb-4 max-h-48"
            data-testid="error-boundary-message"
          >
            {message}
          </pre>
          <button
            type="button"
            onClick={this.handleReload}
            className="px-4 py-2 rounded-md bg-[#2E7D32] text-white text-sm font-semibold hover:bg-[#1B5E20]"
            data-testid="error-boundary-reload"
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
